import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { PLANS, BILLING_ENABLED, planLabel, startCheckout, formatNumber } from "@/lib/plans";
import ContactSalesDialog from "@/components/ContactSalesDialog";

/**
 * Choose plan. Three plans and Enterprise, monthly or annual.
 * Monthly shows the first-month offer ("$4 first month, then $29/mo");
 * annual saves 20% and has no first-month offer.
 * Pay buttons call Stripe Checkout once BILLING_ENABLED is on; until then
 * they are visible but switched off, with a note.
 */
export default function ChoosePlan() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [interval, setBillingInterval] = useState(params.get("interval") === "annual" ? "annual" : "monthly");
  const [busy, setBusy] = useState(null);
  const [salesOpen, setSalesOpen] = useState(false);
  const highlighted = params.get("plan");
  const current = user?.plan || "free";

  const choose = async (plan) => {
    if (!BILLING_ENABLED) return;
    setBusy(plan);
    try {
      await startCheckout({ plan, interval, offer: interval === "monthly" ? "first_month" : undefined });
    } catch (e) {
      toast.error(e.message);
      setBusy(null);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4 md:px-10">
        <div>
          <div className="eyebrow">Billing</div>
          <h1 className="font-display text-2xl tracking-tight">Choose a plan</h1>
        </div>
        <div role="tablist" aria-label="Billing period" className="inline-flex rounded-full border border-border bg-surface-inset p-1">
          {[["monthly", "Monthly"], ["annual", "Annual · save 20%"]].map(([id, label]) => (
            <button key={id} role="tab" aria-selected={interval === id} onClick={() => setBillingInterval(id)}
              data-testid={`interval-${id}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                interval === id ? "bg-primary text-primary-foreground shadow-glow" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-8 md:px-10 md:py-10">
        <div className="mx-auto w-full max-w-6xl space-y-6">
          <p className="max-w-2xl text-[15px] leading-6 text-ink-secondary">
            You're on <span className="font-semibold text-foreground">{planLabel(current)}</span>.
            Credits are spent only when we find the contact, and unused credits roll over every month.
          </p>

          {!BILLING_ENABLED && (
            <div className="rounded-lg border border-border bg-violet-soft px-4 py-3 text-sm text-violet-text" data-testid="billing-off-note">
              Payments open soon. You can compare plans now; the buttons switch on the day payments go live.
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-3">
            {PLANS.map((p) => {
              const isCurrent = current === p.id;
              const featured = highlighted ? highlighted === p.id : p.featured;
              const price = interval === "annual" ? p.annual : p.firstMonth;
              return (
                <div key={p.id} data-testid={`plan-card-${p.id}`}
                  className={`relative flex flex-col rounded-xl border bg-surface p-7 ${
                    featured ? "border-primary shadow-featured" : "border-border shadow-card"}`}>
                  {p.featured && (
                    <span className="absolute -top-3 left-7 rounded-full bg-primary px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground">
                      Most popular
                    </span>
                  )}
                  <div className="font-display text-[22px]">{p.label}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{p.blurb}</p>
                  <div className="mt-5 flex items-end gap-1.5">
                    <span className="font-display text-[52px] leading-none tracking-[-0.03em] tabular-nums">${price}</span>
                    <span className="pb-1.5 text-sm text-muted-foreground">{interval === "annual" ? "/mo, billed yearly" : "first month"}</span>
                  </div>
                  <div className="mt-2 text-[13px] text-ink-secondary">
                    {interval === "annual"
                      ? `$${p.annual * 12} a year · Cancel anytime`
                      : `Then $${p.monthly}/mo · Cancel anytime`}
                  </div>
                  {interval === "monthly" && (
                    <div className="mt-3 w-fit rounded-full bg-success-soft px-2.5 py-0.5 text-xs font-medium text-success">
                      We cover ${p.weCover} of your first month
                    </div>
                  )}
                  <ul className="mt-5 flex-1 space-y-2 text-sm">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-violet-text" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button className="mt-6 w-full" variant={featured ? "default" : "outline"}
                    disabled={isCurrent || !BILLING_ENABLED || busy !== null}
                    onClick={() => choose(p.id)} data-testid={`choose-${p.id}`}>
                    {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" />
                      : isCurrent ? "Your plan"
                      : interval === "annual" ? `Start ${p.label}` : `Start for $${p.firstMonth}`}
                  </Button>
                  <div className="mt-2 text-center text-xs text-muted-foreground tabular-nums">
                    {formatNumber(p.credits)} credits a month
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-surface p-6 shadow-card" data-testid="plan-card-enterprise">
            <div>
              <div className="font-display text-[22px]">Enterprise</div>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                Custom credits, limits and terms for larger teams, with a named contact and invoicing.
              </p>
            </div>
            <Button variant="outline" onClick={() => setSalesOpen(true)} data-testid="contact-sales">Contact Sales</Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Prices in USD. Credits have no cash value. Email 1 credit, direct phone 5, company phone 1, Enrich 1 per match found.
          </p>
        </div>
      </div>
      <ContactSalesDialog open={salesOpen} onClose={() => setSalesOpen(false)} />
    </div>
  );
}
